import { useEffect, useState } from 'react';
import { Button, Groupper, Header, Icon, Input, Pagination, Table } from 'react-frontier';
import { Dropdown } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { InventoryItem, InventoryItemStatus } from '../SiteClasses';
import { CreateInventoryItemModal } from '../components';
import { useBreadcrumbs, useUser } from '../hooks';
import UserAccess from '../UserAccess';
import classNames from 'classnames';
import API from '../API';

const PAGE_SIZE = 20;

var ITEM_STATUS: Record<number, { label: string, color: string | null }> = {
	[InventoryItemStatus.AVAILABLE]: { label: 'Disponible', color: 'green' },
	[InventoryItemStatus.PENDING]: { label: 'En Uso', color: 'orange' },
	[InventoryItemStatus.SHIPPED]: { label: 'En Uso', color: 'orange' },
	[InventoryItemStatus.DELIVERED]: { label: 'En Uso', color: 'orange' },
};

enum StatusFilter{
	AVAILABLE = 1,
	IN_USE = 2,
}

var ItemsList = ()=>{
	var { setTitle } = useBreadcrumbs();
	var { hasAccess } = useUser();
	var [items, setItems] = useState<InventoryItem[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [loading, setLoading] = useState<boolean>(false);
	var [search, setSearch] = useState<string>('');
	var [searchTimeout, setSearchTimeout] = useState<any>(null);
	var [page, setPage] = useState<number>(0);
	var [statusFilters, setStatusFilters] = useState<StatusFilter[]>([]);
	var [itemModal, setItemModal] = useState<boolean>(false);

	useEffect(()=>{
		setTitle('Equipos');
		get();
	}, []);

	useEffect(()=>{
		if(items !== null){
			setPage(0);
			get(0, search.length >= 2 ? search : null);
		}
	}, [statusFilters]);

	var get = (get_page?: number, query?: string)=>{
		var p = get_page ?? page;
		var offset = p * PAGE_SIZE;
		setLoading(true);
		API.getInventoryOutOfWarehouse(offset, PAGE_SIZE, query, statusFilters).then(res=>{
			if(res.error) return setLoadError(res.message);
			setItems(res.data);
		}).catch(()=>{
			setLoadError('Hubo un error inesperado cargando los equipos.');
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onSearchChange = (val: string)=>{
		setSearch(val);
		if(searchTimeout) clearTimeout(searchTimeout);
		if(val.length > 0 && val.length < 2) return;
		setSearchTimeout(setTimeout(()=>{
			setPage(0);
			get(0, val.length >= 2 ? val : null);
		}, 500));
	};

	var onStatusFilter = (s: StatusFilter)=>{
		setStatusFilters(prev=>{
			if(prev.includes(s)) return prev.filter(x => x !== s);
			return [...prev, s];
		});
	};

	var onPageChange = (p: number)=>{
		setPage(p);
		get(p, search.length >= 2 ? search : null);
	};

	if(loadError) return <Header text='Error' subtext={loadError} />;

	var openCreate = ()=>{
		setItemModal(true);
	};

	return <><Groupper title='Equipos' fitted style={{ marginTop: 20 }} width={800}
		titleRight={hasAccess(UserAccess.INVENTORY.CREATE_INVENTORY) && (
			<Button iconName='plus-circle' color='black' text='Agregar' size='small' onClick={openCreate} />
		)}
	>
		<div style={{ display: 'flex', gap: 10, padding: '10px 10px 0' }}>
			<Input
				placeholder='Buscar por ID, nombre, serie o tipo...'
				value={search}
				onChange={onSearchChange}
				style={{ flex: 1 }}
			/>
			<Dropdown icon={null} pointing direction='left' trigger={(
				<Button
					text='Filtros'
					iconRight={statusFilters.length > 0 ? 'asterisk' : null}
					iconRightStyle={{ color: 'brown', marginLeft: 5 }}
					size='small'
					iconName='filter'
				/>
			)}>
				<Dropdown.Menu>
					<Dropdown.Item text='Limpiar filtros' icon='trash' onClick={()=>setStatusFilters([])} />
					<Dropdown.Divider />
					<Dropdown.Header content='Estatus' />
					{([
						[StatusFilter.AVAILABLE, 'Disponible'],
						[StatusFilter.IN_USE, 'En Uso'],
					] as [StatusFilter, string][]).map(([s, label])=>(
						<Dropdown.Item key={s} onClick={()=>onStatusFilter(s)}>
							<i className={classNames('icon', {
								'check square': statusFilters.includes(s),
								'square outline': !statusFilters.includes(s),
							})}></i>
							{label}
						</Dropdown.Item>
					))}
				</Dropdown.Menu>
			</Dropdown>
		</div>
		<Table
			striped
			fitted
			headers={['ID', '', 'Tipo', 'Nombre', 'Descripción', 'Ubicación', 'Estatus', '']}
			collapsingIndexes={[0, 1, 6]}
			centeredIndexes={[5]}
			footer={items && (items.length >= PAGE_SIZE || page > 0) && (
				<Table.Cell row colSpan={8} style={{ textAlign: 'right' }}>
					<Pagination page={page} onPageChange={onPageChange} hasNext={items.length >= PAGE_SIZE} />
				</Table.Cell>
			)}
		>
			{loading || !items ? (
				<Table.Cell row colSpan={8}>
					<Header loading text='Cargando' />
				</Table.Cell>
			) : items.length === 0 ? (
				<Table.Cell row className='empty' colSpan={8}>
					No se encontraron registros
				</Table.Cell>
			) : items.map(item=>(
				<Table.Row
					key={item.item_id}
					collapsingIndexes={[0, 1, 6]}
					centeredIndexes={[5]}
					data={[
						item.item_id,
						<Icon name={item.type_icon as any} />,
						item.type_name,
						item.item_name || <Icon name='minus' />,
						item.description || <Icon name='minus' />,
						item.warehouse_name || item.location_city || <Icon name='minus' />,
						item.status
							? <div className={classNames('fr label', ITEM_STATUS[item.status]?.color)}>{ITEM_STATUS[item.status]?.label}</div>
							: <Icon name='minus' />,
						<Dropdown icon={(
							<div className='fr label' style={{ padding: '3px 10px' }}>
								<Icon name='ellipsis' style={{ margin: 0 }} />
							</div>
						)} className='icon'>
							<Dropdown.Menu>
								<Dropdown.Item as={Link} to={`/item/${item.item_id}`}>
									<Icon name='eye' style={{ marginRight: 3 }} /> Ver
								</Dropdown.Item>
								{item.shipment_id && (
									<Dropdown.Item as={Link} to={`/shipments/${item.shipment_id}`}>
										<Icon name='truck' style={{ marginRight: 3 }} /> Ver envío
									</Dropdown.Item>
								)}
							</Dropdown.Menu>
						</Dropdown>,
					]}
				/>
			))}
		</Table>
	</Groupper>
	<CreateInventoryItemModal
		open={itemModal}
		onClose={()=>setItemModal(false)}
		onCreate={()=>{ setItemModal(false); get(0, search.length >= 2 ? search : null); }}
	/>
	</>;
};

export default ItemsList;
